import { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Input,
  Table,
  Button,
  Space,
  Alert,
  Spin,
  Typography,
  Row,
  Col,
  Divider,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import { billingApi, BillingService } from "~/services/billing/billing.api";
import { createInvoice } from "~/services/billing/billing.api";
import { useNotification } from "~/hooks";

const { Title, Text } = Typography;

interface CustomLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

interface InvoiceCreationProps {
  bookingId: string;
}

export const InvoiceCreation = ({ bookingId }: InvoiceCreationProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [billingData, setBillingData] = useState<BillingService[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [customLineItems, setCustomLineItems] = useState<CustomLineItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { success, error: notificationError } = useNotification();
  const navigate = useNavigate();

  const loadBillingData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await billingApi.getBookingBillingData(bookingId);
      setBillingData(response.services);

      // Auto-select all services by default
      const serviceIds = response.services.map((service) => service.id);
      setSelectedServices(serviceIds);
    } catch (error) {
      console.error("Error loading billing data:", error);
      notificationError({
        message: "Error",
        description: "Failed to load billing data",
      });
    } finally {
      setLoading(false);
    }
  }, [bookingId, notificationError]);

  useEffect(() => {
    if (bookingId) {
      loadBillingData();
    }
  }, [bookingId, loadBillingData]);

  const handleServiceSelection = (serviceId: string, checked: boolean) => {
    if (checked) {
      setSelectedServices((prev) => [...prev, serviceId]);
    } else {
      setSelectedServices((prev) => prev.filter((id) => id !== serviceId));
    }
  };

  const getSelectedServicesData = () => {
    return billingData.filter((service) =>
      selectedServices.includes(service.id)
    );
  };

  const calculateInvoiceLineItems = () => {
    const lineItems: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
    }> = [];
    const selectedServicesData = getSelectedServicesData();

    // Add selected services as line items using shared billing API
    const serviceLineItems =
      billingApi.calculateLineItems(selectedServicesData);
    lineItems.push(...serviceLineItems);

    // Add custom line items
    customLineItems.forEach((item) => {
      lineItems.push({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      });
    });

    return lineItems;
  };

  const calculateTotals = () => {
    const selectedServicesData = getSelectedServicesData();

    // Get tax preference from localStorage for this booking
    const savedTaxPreference = localStorage.getItem(
      `tax_preference_${bookingId}`
    );
    const applyTax =
      savedTaxPreference !== null ? JSON.parse(savedTaxPreference) : false;

    const servicesTotals = billingApi.calculateTotals(
      selectedServicesData,
      bookingId,
      applyTax
    );

    const customTotal = customLineItems.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0
    );

    const subtotal = servicesTotals.subtotal + customTotal;
    // Use the tax calculation from billingApi which already handles service-specific tax rules
    const tax = servicesTotals.tax + (applyTax ? customTotal * 0.08875 : 0); // Apply 8.875% tax to custom items only if applyTax is true
    const total = subtotal + tax;

    return { subtotal, tax, total };
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);

      const lineItems = calculateInvoiceLineItems();
      if (lineItems.length === 0) {
        notificationError({
          message: "No Line Items",
          description:
            "Please select at least one service or add custom line items.",
        });
        return;
      }

      // Create invoice with basic structure
      const invoiceData = {
        bookingId,
        customerRef: values.customerRef,
        lineItems,
        notes: values.notes,
      };

      // Use a basic API call
      await createInvoice(invoiceData as any);

      success({
        message: "Invoice Created",
        description: "Invoice has been created successfully.",
      });

      navigate("/billing/invoicing");
    } catch (error) {
      console.error("Error creating invoice:", error);
      notificationError({
        message: "Error",
        description: "Failed to create invoice. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const addCustomLineItem = () => {
    const newItem: CustomLineItem = {
      id: `custom-${Date.now()}`,
      description: "",
      quantity: 1,
      unitPrice: 0,
    };
    setCustomLineItems((prev) => [...prev, newItem]);
  };

  const updateCustomLineItem = (
    id: string,
    field: keyof CustomLineItem,
    value: any
  ) => {
    setCustomLineItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const removeCustomLineItem = (id: string) => {
    setCustomLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  const serviceColumns: ColumnsType<BillingService> = [
    {
      title: "Select",
      width: 60,
      render: (_, service) => (
        <input
          type="checkbox"
          checked={selectedServices.includes(service.id)}
          onChange={(e) => handleServiceSelection(service.id, e.target.checked)}
        />
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string, service: BillingService) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-sm text-gray-500">
            <Space size={16}>
              <span>
                Date: {new Date(service.serviceDate).toLocaleDateString()}
              </span>
              {/* Only show total hours for staff services (COLLECTION, DELIVERY, ON_SITE_SERVICE) */}
              {(service.serviceType === "COLLECTION" ||
                service.serviceType === "DELIVERY" ||
                service.serviceType === "ON_SITE_SERVICE") &&
                service.totalHours > 0 && (
                  <span>Total Hours: {service.totalHours}h</span>
                )}
            </Space>
          </div>
          {/* Show breakdown for collection/delivery services like booking detail */}
          {(service.serviceType === "COLLECTION" ||
            service.serviceType === "DELIVERY") &&
            service.calculationDetails?.staffCosts &&
            service.calculationDetails.staffCosts.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                {/* Show combined breakdown like booking detail */}
                <div className="ml-4">
                  ↳ {service.calculationDetails.staffCosts[0].quantity}x{" "}
                  {service.calculationDetails.staffCosts[0].staffRoleName}s +{" "}
                  {service.calculationDetails?.vesselCost &&
                    service.calculationDetails.vesselCost > 0
                    ? "Sprinter"
                    : "Vehicle"}
                  , rate{" "}
                  {billingApi.formatCurrency(
                    service.calculationDetails.staffCosts.reduce(
                      (total: number, staff: any) =>
                        total + staff.hourlyRate * staff.quantity,
                      0
                    ) +
                    (service.calculationDetails.vesselCost
                      ? service.calculationDetails.vesselCost /
                      (service.calculationDetails.hoursWorked || 1)
                      : 0)
                  )}
                  /hr × {service.totalHours}h
                </div>
              </div>
            )}
        </div>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
      render: (qty: number) => qty || 1,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      render: (rate: number) => billingApi.formatCurrency(rate || 0),
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 100,
      render: (amount: number) => billingApi.formatCurrency(amount || 0),
    },
  ];

  const customLineItemColumns: ColumnsType<CustomLineItem> = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string, record: CustomLineItem) => (
        <Input
          value={text}
          placeholder="Enter description"
          onChange={(e) =>
            updateCustomLineItem(record.id, "description", e.target.value)
          }
        />
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (qty: number, record: CustomLineItem) => (
        <Input
          type="number"
          value={qty}
          min={1}
          onChange={(e) =>
            updateCustomLineItem(record.id, "quantity", Number(e.target.value))
          }
        />
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 120,
      render: (price: number, record: CustomLineItem) => (
        <Input
          type="number"
          value={price}
          min={0}
          step={0.01}
          prefix="$"
          onChange={(e) =>
            updateCustomLineItem(record.id, "unitPrice", Number(e.target.value))
          }
        />
      ),
    },
    {
      title: "Amount",
      key: "amount",
      width: 100,
      render: (_, record: CustomLineItem) =>
        billingApi.formatCurrency(record.quantity * record.unitPrice),
    },
    {
      title: "Action",
      width: 80,
      render: (_, record: CustomLineItem) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeCustomLineItem(record.id)}
        />
      ),
    },
  ];

  const totals = calculateTotals();

  return (
    <div className="p-6">
      <Card>
        {/* Header */}
        <div className="mb-6">
          <Row align="middle" justify="space-between">
            <Col>
              <div className="flex items-center space-x-3">
                <FileTextOutlined className="text-2xl text-blue-500" />
                <div>
                  <Title level={3} className="mb-0">
                    Create Invoice
                  </Title>
                  <Text type="secondary">Booking ID: {bookingId}</Text>
                </div>
              </div>
            </Col>
            <Col>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/billing/invoicing")}
                type="default"
              >
                Back to Invoicing
              </Button>
            </Col>
          </Row>
        </div>

        <Divider />

        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              customerRef: "",
              notes: "",
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Customer Reference" name="customerRef">
                  <Input placeholder="Enter customer reference" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Notes" name="notes">
                  <Input.TextArea
                    placeholder="Enter any additional notes"
                    rows={2}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Service Items */}
            <div className="mb-6">
              <Title level={4}>Service Items</Title>
              {billingData.length > 0 ? (
                <Table
                  columns={serviceColumns}
                  dataSource={billingData}
                  pagination={false}
                  size="small"
                  rowKey="id"
                  scroll={{ x: true }}
                />
              ) : (
                <Alert
                  message="No billing data available"
                  description="Unable to load service information for this booking."
                  type="warning"
                  showIcon
                />
              )}
            </div>

            {/* Custom Line Items */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <Title level={4}>Custom Line Items</Title>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addCustomLineItem}
                >
                  Add Line Item
                </Button>
              </div>

              {customLineItems.length > 0 && (
                <Table
                  columns={customLineItemColumns}
                  dataSource={customLineItems}
                  pagination={false}
                  size="small"
                  rowKey="id"
                />
              )}
            </div>

            {/* Totals */}
            <Card className="mb-6" style={{ backgroundColor: "#f5f5f5" }}>
              <Row gutter={16}>
                <Col span={18}>
                  <div className="text-right">
                    <div className="mb-2">
                      <Text strong>
                        Subtotal: {billingApi.formatCurrency(totals.subtotal)}
                      </Text>
                    </div>
                    {totals.tax > 0 && (
                      <div className="mb-2">
                        <Text>
                          Tax (8.875%): {billingApi.formatCurrency(totals.tax)}
                        </Text>
                        {(() => {
                          const savedTaxPreference = localStorage.getItem(
                            `tax_preference_${bookingId}`
                          );
                          const applyTax =
                            savedTaxPreference !== null
                              ? JSON.parse(savedTaxPreference)
                              : false;

                          return (
                            <div className="text-xs text-gray-500 mt-1">
                              Tax Status: {applyTax ? "Applied" : "Excluded"}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    <div>
                      <Text strong style={{ fontSize: "18px" }}>
                        Total: {billingApi.formatCurrency(totals.total)}
                      </Text>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Button onClick={() => navigate("/billing/invoicing")}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={
                  selectedServices.length === 0 && customLineItems.length === 0
                }
                size="large"
              >
                Create Invoice
              </Button>
            </div>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};
